import { getCanonicalLocale } from '@nextcloud/l10n';
import { getCurrentUser } from '@nextcloud/auth';
import { getLoggerBuilder } from '@nextcloud/logger';

/**
 * @copyright 2019 Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @author Christoph Wurst <christoph@winzerhof-wurst.at>
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
const humanList = ['B', 'KB', 'MB', 'GB', 'TB'];
/**
 * Format a file size in a human-like format. e.g. 42GB
 *
 * @param size in bytes
 * @param skipSmallSizes avoid rendering tiny sizes and return '< 1 KB' instead
 */
function formatFileSize(size, skipSmallSizes = false) {
    if (typeof size === 'string') {
        size = Number(size);
    }
    // Calculate Log with base 1024: size = 1024 ** order
    let order = size > 0 ? Math.floor(Math.log(size) / Math.log(1024)) : 0;
    // Stay in range of the byte sizes that are defined
    order = Math.min(humanList.length - 1, order);
    const readableFormat = humanList[order];
    let relativeSize = (size / Math.pow(1024, order)).toFixed(1);
    if (skipSmallSizes === true && order === 0) {
        if (relativeSize !== '0.0') {
            return '< 1 KB';
        }
        else {
            return '0 KB';
        }
    }
    if (order < 2) {
        relativeSize = parseFloat(relativeSize).toFixed(0);
    }
    else {
        relativeSize = parseFloat(relativeSize).toLocaleString(getCanonicalLocale());
    }
    return relativeSize + ' ' + readableFormat;
}

/**
 * @copyright 2019 Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @author Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
const getLogger = user => {
    if (user === null) {
        return getLoggerBuilder()
            .setApp('files')
            .build();
    }
    return getLoggerBuilder()
        .setApp('files')
        .setUid(user.uid)
        .build();
};
var logger = getLogger(getCurrentUser());

/**
 * @copyright Copyright (c) 2021 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
class NewFileMenu {
    _entries = [];
    registerEntry(entry) {
        this.validateEntry(entry);
        this._entries.push(entry);
    }
    unregisterEntry(entry) {
        const entryIndex = typeof entry === 'string'
            ? this.getEntryIndex(entry)
            : this.getEntryIndex(entry.id);
        if (entryIndex === -1) {
            logger.warn('Entry not found, nothing removed', { entry, entries: this.getEntries() });
            return;
        }
        this._entries.splice(entryIndex, 1);
    }
    /**
     * Get the list of registered entries
     *
     * @param {FileInfo} context the creation context. Usually the current folder FileInfo
     */
    getEntries(context) {
        if (context) {
            return this._entries
                .filter(entry => typeof entry.if === 'function' ? entry.if(context) : true);
        }
        return this._entries;
    }
    getEntryIndex(id) {
        return this._entries.findIndex(entry => entry.id === id);
    }
    validateEntry(entry) {
        if (!entry.id || !entry.displayName || !(entry.iconSvgInline || entry.iconClass)) {
            throw new Error('Invalid entry');
        }
        if (typeof entry.id !== 'string'
            || typeof entry.displayName !== 'string') {
            throw new Error('Invalid id or displayName property');
        }
        if ((entry.iconClass && typeof entry.iconClass !== 'string')
            || (entry.iconSvgInline && typeof entry.iconSvgInline !== 'string')) {
            throw new Error('Invalid icon provided');
        }
        if (entry.if !== undefined && typeof entry.if !== 'function') {
            throw new Error('Invalid if property');
        }
        if (entry.templateName && typeof entry.templateName !== 'string') {
            throw new Error('Invalid templateName property');
        }
        if (entry.handler && typeof entry.handler !== 'function') {
            throw new Error('Invalid handler property');
        }
        if (!entry.templateName && !entry.handler) {
            throw new Error('At least a templateName or a handler must be provided');
        }
        if (this.getEntryIndex(entry.id) !== -1) {
            throw new Error('Duplicate entry');
        }
    }
}
const getNewFileMenu = function () {
    if (typeof window._nc_newfilemenu === 'undefined') {
        window._nc_newfilemenu = new NewFileMenu();
        logger.debug('NewFileMenu initialized');
    }
    return window._nc_newfilemenu;
};

/**
 * @copyright 2019 Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @author Christoph Wurst <christoph@winzerhof-wurst.at>
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
/**
 * Add a new menu entry to the upload manager menu
 */
const addNewFileMenuEntry = function (entry) {
    const newFileMenu = getNewFileMenu();
    return newFileMenu.registerEntry(entry);
};
/**
 * Remove a previously registered entry from the upload menu
 */
const removeNewFileMenuEntry = function (entry) {
    const newFileMenu = getNewFileMenu();
    return newFileMenu.unregisterEntry(entry);
};
/**
 * Get the list of registered entries from the upload menu
 *
 * @param {FileInfo} context the creation context. Usually the current folder FileInfo
 */
const getNewFileMenuEntries = function (context) {
    const newFileMenu = getNewFileMenu();
    return newFileMenu.getEntries(context);
};

export { addNewFileMenuEntry, formatFileSize, getNewFileMenuEntries, removeNewFileMenuEntry };
//# sourceMappingURL=index.esm.js.map
